// Shared project catalogue used by both /projects (grid + filter) and
// /projects/[slug] (detail). All entries are placeholders until Cris
// confirms the final clothing-brand case-study list — see
// docs/content/products.md for the broader context.

export type ProjectCategory = "full-launch" | "branding" | "shopify" | "seo-content";

export type ProjectKpi = { value: string; label: string };

export type Project = {
  slug: string;
  title: string;
  category: ProjectCategory;
  summary: string;
  scope: string[];
  year: string;
  client?: string;
  founderQuote?: string;
  founderName?: string;
  liveUrl?: string;
  results?: ProjectKpi[];
  coverImage?: string;
};

export const projects: Project[] = [
  {
    slug: "brand-1",
    title: "Brand Name 1",
    category: "full-launch",
    summary: "End-to-end clothing-brand launch — identity, Shopify storefront, baseline SEO, first-month content.",
    scope: ["Branding", "Shopify", "SEO"],
    year: "2025",
    client: "Clothing brand founder, UK",
    founderQuote: "We went from a Pinterest board to a live Shopify store in eight weeks. First sale came three days after launch.",
    founderName: "Founder, full launch",
    results: [
      { value: "8 wks", label: "Idea to launch" },
      { value: "3 days", label: "First sale post-launch" },
      { value: "+62%", label: "Add-to-cart on revised product pages" },
      { value: "12", label: "Keywords ranking month 3" },
    ],
    coverImage: "/images/projects/brand-1.webp",
  },
  {
    slug: "brand-2",
    title: "Brand Name 2",
    category: "branding",
    summary: "Brand identity + guidelines pack for a streetwear label launching from London.",
    scope: ["Logo", "Guidelines"],
    year: "2025",
    client: "Streetwear founder, London",
    results: [
      { value: "3 wks", label: "Concept to delivery" },
      { value: "5", label: "Application surfaces (tee, tag, IG, hat, web)" },
      { value: "1", label: "Round of major revisions" },
    ],
    coverImage: "/images/projects/brand-2.webp",
  },
  {
    slug: "brand-3",
    title: "Brand Name 3",
    category: "seo-content",
    summary: "Twelve-month SEO and content retainer driving organic traffic for a UK-made loungewear brand.",
    scope: ["SEO", "Blog"],
    year: "2025",
    client: "Loungewear brand, Manchester",
    results: [
      { value: "+340%", label: "Organic traffic vs baseline" },
      { value: "26", label: "Keywords on page 1" },
      { value: "48", label: "Articles published in year 1" },
      { value: "4.2×", label: "Repeat-visit rate" },
    ],
    coverImage: "/images/projects/brand-3.webp",
  },
  {
    slug: "brand-4",
    title: "Brand Name 4",
    category: "full-launch",
    summary: "Full clothing-brand setup — from name research through to live store and first paid campaigns.",
    scope: ["Branding", "Shopify"],
    year: "2024",
    client: "Activewear founder, Birmingham",
    results: [
      { value: "10 wks", label: "Total project length" },
      { value: "£8k", label: "First-month revenue" },
      { value: "120", label: "Email subscribers pre-launch" },
    ],
    coverImage: "/images/projects/brand-4.webp",
  },
  {
    slug: "brand-5",
    title: "Brand Name 5",
    category: "branding",
    summary: "Logo, palette, and packaging for a sustainable basics label.",
    scope: ["Logo", "Packaging"],
    year: "2024",
    client: "Sustainable basics brand",
    results: [
      { value: "2 wks", label: "Brand identity delivery" },
      { value: "6", label: "Packaging variants designed" },
    ],
    coverImage: "/images/projects/brand-5.webp",
  },
  {
    slug: "brand-6",
    title: "Brand Name 6",
    category: "full-launch",
    summary: "Pre-launch package — branding, store, copy, photography direction.",
    scope: ["Branding", "Shopify", "SEO"],
    year: "2024",
    client: "Womenswear founder, Bristol",
    founderQuote: "I came in with sketches on my phone and a Shopify trial. Eight weeks later I had a brand, a store, and orders shipping.",
    founderName: "Founder, womenswear",
    results: [
      { value: "8 wks", label: "Sketches to live store" },
      { value: "18", label: "Products live at launch" },
      { value: "+44%", label: "Conversion vs benchmark" },
    ],
    coverImage: "/images/projects/brand-6.webp",
  },
];

export const categoryFilters = [
  { slug: "all" as const, label: "All" },
  { slug: "full-launch" as const, label: "Full launch" },
  { slug: "branding" as const, label: "Branding" },
  { slug: "shopify" as const, label: "Shopify" },
  { slug: "seo-content" as const, label: "SEO & Content" },
];

export const categoryLabel: Record<ProjectCategory, string> = {
  "full-launch": "Full launch",
  branding: "Branding",
  shopify: "Shopify",
  "seo-content": "SEO & Content",
};
